'use client';

/**
 * The /create-comic flow (moved from app/create-comic/page.tsx, make-up-your-own Task 2). The page is a server
 * component that parses ?story= / ?style= / ?idea= / ?occasion= and passes them in.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { MangaPhotoStep } from '@/components/manga/MangaPhotoStep';
import { MangaCharactersStep } from '@/components/manga/MangaCharactersStep';
import { MangaStyleStep } from '@/components/manga/MangaStyleStep';
import { MangaGenerateStep } from '@/components/manga/MangaGenerateStep';
import {
  charactersFromFaces,
  MangaCharacterDraft,
  MangaPhoto,
} from '@/components/manga/types';
import { MangaSettings, MangaSubStyle, MANGA_DEFAULTS, resolveMangaSettings } from '@/types/manga';
import { trackCreate } from '@/utils/create-flow-analytics';
import type { TypedRelativesChoice } from '@/utils/manga/family-guard';
import { ideaSourceFor, type IdeaOccasion, type IdeaPick } from '@/lib/catalogue/idea-cards';
import type { ComicSeed } from '@/lib/catalogue/comic-seeds';
import { FormatHeader } from '@/components/create-flow/FormatHeader';
import { FormatPreviewHeading } from '@/components/create-flow/FormatPreviewHeading';
import { FormatMismatchNotice } from '@/components/create-flow/FormatMismatchNotice';
import { useFormatSwitch } from '@/components/create-flow/use-format-switch';
import { clearFormatHandoff, handoffNote, peekFormatHandoff } from '@/lib/create-flow/format-handoff';
import { formatMismatch, nextPromptState, ownText, promptVisible, type MismatchPromptState } from '@/lib/create-flow/format-mismatch';
import { comicStoryIdFor, payloadLanguage } from '@/components/manga/preview-payload';
import { capturePartnerCode } from '@/lib/partners/capture-partner-code';

const STEP_NAMES = ['photo', 'people', 'style', 'preview'] as const;

const STEPS = [
  { number: 1, label: 'Photo' },
  { number: 2, label: 'Heroes' },
  { number: 3, label: 'Style' },
  { number: 4, label: 'Generate' },
];

export interface CreateComicFlowProps {
  /** A valid ?style= pre-selects that sub-style (Western styles stay left to right). */
  initialStyle?: MangaSubStyle;
  /** A known comic idea id, or 'surprise' (?idea=). */
  initialIdea?: string;
  initialOccasion?: IdeaOccasion;
  /** An offered catalogue story (?story=, resolved server-side by offeredComicSeed); it wins over ?idea. */
  initialStory?: ComicSeed;
  /** `?language=`, already validated by the page. */
  initialLanguage?: string;
}

export function CreateComicFlow({ initialStyle, initialIdea, initialOccasion, initialStory, initialLanguage }: CreateComicFlowProps) {
  // A switch from the picture-book flow: peeked here, cleared in an effect (strict mode renders and mounts twice)
  const [handoff] = useState(() => (initialStory ? null : peekFormatHandoff('comic')));
  useEffect(() => {
    clearFormatHandoff();
  }, []);
  // A partner link's code is kept for checkout (the picture-book flow does the same)
  useEffect(() => {
    capturePartnerCode(window.location.search);
  }, []);
  const switchFormat = useFormatSwitch('comic');
  const [step, setStep] = useState(1);
  const [photo, setPhoto] = useState<MangaPhoto | null>(handoff?.photo ?? null);
  const [characters, setCharacters] = useState<MangaCharacterDraft[]>(handoff?.characters ?? []);
  // The chosen catalogue story (stories-as-comics Task 3) and the text it last filled in
  const [story, setStory] = useState<ComicSeed | null>(initialStory ?? null);
  const [storyPick, setStoryPick] = useState<IdeaPick | null>(null);
  const [settings, setSettings] = useState<MangaSettings>(() =>
    resolveMangaSettings({
      ...MANGA_DEFAULTS,
      subStyle: initialStory?.subStyle ?? initialStyle ?? MANGA_DEFAULTS.subStyle,
      ...(initialStory ? { colorMode: initialStory.colorMode, pageCount: initialStory.pageCount } : {}),
    }),
  );
  const [storyTitle, setStoryTitle] = useState(handoff?.title ?? '');
  const [storyIdea, setStoryIdea] = useState(handoff?.idea ?? '');
  // The idea card that filled the title and idea (Task 3); compared at Next to tell kept from edited
  const [ideaPick, setIdeaPick] = useState<IdeaPick | null>(null);
  // The pick + text whose suggestion_kept/edited events were sent, so Back then Next does not send them again
  const [ideaEventsKey, setIdeaEventsKey] = useState<string | null>(null);
  // The parent's answer to the typed-relative notice (Task C3); it only counts for the words it was given for
  const [typedRelatives, setTypedRelatives] = useState<TypedRelativesChoice | null>(null);
  // Same list as the picture-book wizard; English by default.
  const [language, setLanguage] = useState(handoff?.language ?? initialLanguage ?? 'en');
  // Preview books already made, keyed by input hash: Back then Next re-polls instead of paying again.
  const [previewBooks, setPreviewBooks] = useState<Record<string, string>>({});

  useEffect(() => {
    trackCreate('comic', 'create_step_viewed', { step: STEP_NAMES[step - 1] });
  }, [step]);

  // The picture-book-word check reads only what the parent wrote (never a card, a story or a carried idea)
  const [prompt, setPrompt] = useState<MismatchPromptState>('idle');
  const matches = formatMismatch(
    ownText([
      { text: storyTitle, ours: [ideaPick?.title, storyPick?.title, handoff?.title] },
      { text: storyIdea, ours: [ideaPick?.idea, storyPick?.idea, handoff?.idea] },
    ]),
    'comic',
    language,
  );
  const shownTracked = useRef(false);
  useEffect(() => {
    if (step !== 3) return;
    const next = nextPromptState(prompt, matches);
    if (next === prompt) return;
    setPrompt(next);
    if (prompt === 'idle' && next === 'shown' && !shownTracked.current) {
      shownTracked.current = true;
      trackCreate('comic', 'format_mismatch_prompt', { step: 'style', action: 'shown' });
    }
  }, [matches, step, prompt]);
  const reshow = step === 4 && promptVisible(prompt, matches);
  const previewShownTracked = useRef(false);
  useEffect(() => {
    if (!reshow) {
      previewShownTracked.current = false;
      return;
    }
    if (previewShownTracked.current) return;
    previewShownTracked.current = true;
    trackCreate('comic', 'format_mismatch_prompt', { step: 'preview', action: 'shown' });
  }, [reshow]);

  const snapshot = () => ({ photo, characters, title: storyTitle, idea: storyIdea, language });
  const onSwitch = (source: 'card' | 'strip' | 'preview') => () => switchFormat(snapshot(), { source, step: STEP_NAMES[step - 1] });
  const accept = (at: 'style' | 'preview') => () => {
    trackCreate('comic', 'format_mismatch_prompt', { step: at, action: 'accepted' });
    setPrompt('accepted');
    switchFormat(snapshot(), { source: 'mismatch', step: at });
  };
  const dismiss = (at: 'style' | 'preview') => () => {
    trackCreate('comic', 'format_mismatch_prompt', { step: at, action: 'dismissed' });
    setPrompt('dismissed');
    document.getElementById(at === 'style' ? 'manga-story-idea' : 'comic-preview-format')?.focus();
  };

  const handlePhotoChange = (newPhoto: MangaPhoto | null) => {
    // A different photo means the detected faces (and pre-filled heroes) are stale
    if (!newPhoto || newPhoto.dataUrl !== photo?.dataUrl) {
      setCharacters([]);
    }
    setPhoto(newPhoto);
  };

  const handlePhotoNext = () => {
    if (!photo) return;
    if (characters.length === 0) {
      setCharacters(charactersFromFaces(photo.faces, photo.analysis));
    }
    setStep(2);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 py-8 px-4 sm:px-6 lg:px-8"
      data-page="create-comic"
    >
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center space-y-1">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Make a <strong>comic</strong> starring your family
          </h1>
          <p className="text-gray-600">
            Turn your family into comic book heroes — panels, speech balloons and sound effects included.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center">
          {STEPS.map((s, index) => (
            <React.Fragment key={s.number}>
              {index > 0 && (
                <div
                  className={`h-0.5 w-6 sm:w-12 mx-1 sm:mx-2 ${
                    step > s.number - 1 ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                />
              )}
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                    step > s.number
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : step === s.number
                        ? 'bg-white border-purple-600 text-purple-700'
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {step > s.number ? <Check className="w-4 h-4" /> : s.number}
                </div>
                <span
                  className={`text-xs mt-1 ${
                    step >= s.number ? 'text-purple-700 font-medium' : 'text-gray-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {step === 1 ? (
          <FormatHeader
            format="comic"
            variant="card"
            onSwitch={onSwitch('card')}
            note={handoff ? handoffNote(handoff) : null}
            focusOnMount={!!handoff}
          />
        ) : step === 4 ? null : (
          <FormatHeader format="comic" variant="strip" onSwitch={onSwitch('strip')} />
        )}

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-8">
          {step === 1 && (
            <MangaPhotoStep
              photo={photo}
              onPhotoChange={handlePhotoChange}
              onNext={handlePhotoNext}
            />
          )}

          {step === 2 && (
            <MangaCharactersStep
              characters={characters}
              onCharactersChange={setCharacters}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}

          {step === 3 && (
            <MangaStyleStep
              settings={settings}
              onSettingsChange={setSettings}
              storyTitle={storyTitle}
              onStoryTitleChange={setStoryTitle}
              storyIdea={storyIdea}
              onStoryIdeaChange={setStoryIdea}
              language={language}
              onLanguageChange={setLanguage}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
              characters={characters}
              typedRelatives={typedRelatives}
              onTypedRelativesChange={setTypedRelatives}
              onChangePhoto={() => setStep(1)}
              ideaPick={ideaPick}
              onIdeaPickChange={setIdeaPick}
              pinnedIdea={handoff ? undefined : initialIdea}
              occasion={initialOccasion}
              ideaEventsKey={ideaEventsKey}
              onIdeaEventsSent={setIdeaEventsKey}
              story={story}
              storyPick={storyPick}
              onStoryPickChange={setStoryPick}
              onClearStory={() => setStory(null)}
              ideaNotice={
                promptVisible(prompt, matches) ? (
                  <FormatMismatchNotice flow="comic" onSwitch={accept('style')} onDismiss={dismiss('style')} />
                ) : null
              }
            />
          )}

          {step === 4 && photo && (
            <div className="mb-4 space-y-3">
              <div id="comic-preview-format" tabIndex={-1} className="outline-none">
                <FormatPreviewHeading format="comic" onSwitch={reshow ? undefined : onSwitch('preview')} />
              </div>
              {reshow && <FormatMismatchNotice flow="comic" onSwitch={accept('preview')} onDismiss={dismiss('preview')} />}
            </div>
          )}
          {step === 4 && photo && (
            <MangaGenerateStep
              photo={photo}
              characters={characters}
              settings={settings}
              storyTitle={storyTitle}
              storyIdea={storyIdea}
              language={language}
              typedRelatives={typedRelatives}
              knownBookIds={previewBooks}
              onBookId={(key, id) =>
                setPreviewBooks((prev) => {
                  const next = { ...prev };
                  if (id) next[key] = id;
                  else delete next[key];
                  return next;
                })
              }
              onBack={() => setStep(3)}
              story={story && storyPick ? { id: story.id, idea: storyPick.idea, pageCount: story.pageCount } : null}
              ideaSource={
                story && storyPick && comicStoryIdFor({ id: story.id, idea: storyPick.idea, pageCount: story.pageCount }, storyIdea, settings.pageCount, payloadLanguage(language))
                  ? `story:${story.id}`
                  : ideaSourceFor(ideaPick, { title: storyTitle, idea: storyIdea })
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
